/**
 * Utilities to extract template info from chat messages.
 *
 * A message can carry template info in two places:
 * 1. Message-level fields (`activeTemplate`, `templateData`)
 * 2. Inside the last card in the `content` array
 *
 * These helpers check both, preferring message-level fields.
 */
import type { TemplateType, TemplateData, ActiveTemplate } from '@contracts';
import { DATA_ONLY_TEMPLATE_TYPES } from '@/features/chat/constants/templateTypes';

export type ChatMessage = {
  role: string;
  content?: unknown;
  activeTemplate?: unknown;
  templateData?: unknown;
};

/**
 * Resolve the template type for a message.
 * Checks message-level `activeTemplate` first, then falls back to the last card in content.
 */
export function resolveMessageTemplate(message: ChatMessage): TemplateType | null {
  if (message.activeTemplate && typeof message.activeTemplate === 'string') {
    return message.activeTemplate as TemplateType;
  }

  if (message.role !== 'assistant' || !Array.isArray(message.content)) return null;

  const card = [...message.content]
    .reverse()
    .find((c) => c && typeof c === 'object' && 'templateType' in c);

  return card ? ((card as { templateType?: TemplateType }).templateType ?? null) : null;
}

/**
 * Resolve the template data for a message.
 * Checks message-level `templateData` first, then falls back to the last card in content.
 */
export function resolveMessageTemplateData(message: ChatMessage): TemplateData | null {
  if (message.templateData && typeof message.templateData === 'object') {
    return message.templateData as TemplateData;
  }

  if (message.role !== 'assistant' || !Array.isArray(message.content)) return null;

  const card = [...message.content]
    .reverse()
    .find((c) => c && typeof c === 'object' && 'templateData' in c);

  return card ? ((card as { templateData?: TemplateData }).templateData ?? null) : null;
}

/**
 * Given the current activeTemplate and the message history, return the correct
 * renderable template + data. If the current activeTemplate is a data-only type,
 * scans messages in reverse to find the last renderable one.
 */
export function resolveRenderableTemplate(
  activeTemplate: ActiveTemplate,
  templateData: TemplateData | null | undefined,
  messages: ChatMessage[],
): { activeTemplate: ActiveTemplate; templateData: TemplateData | null } {
  if (!activeTemplate || !DATA_ONLY_TEMPLATE_TYPES.has(activeTemplate)) {
    return { activeTemplate, templateData: templateData ?? null };
  }

  const lastRenderableMessage = [...messages].reverse().find((message) => {
    const template = resolveMessageTemplate(message);
    return template && !DATA_ONLY_TEMPLATE_TYPES.has(template);
  });

  return {
    activeTemplate: lastRenderableMessage
      ? (resolveMessageTemplate(lastRenderableMessage) as ActiveTemplate)
      : null,
    templateData: lastRenderableMessage
      ? (resolveMessageTemplateData(lastRenderableMessage) ?? null)
      : null,
  };
}
